import { AlertCircle, Loader2, MessageCircle } from "lucide-react";
import { type ReactElement, useEffect, useRef } from "react";

import { cn } from "@/lib/utils";

import type { ActionResponse, ChatError, ChatMessage } from "../types";
import { extractCitations } from "../utils/citations";
import { CitationReference } from "./CitationReference";
import { MarkdownRenderer } from "./MarkdownRenderer";
import { PrefillCard } from "./PrefillCard";

interface ChatMessagesProps {
  messages: ChatMessage[];
  isLoading: boolean;
  isStreaming: boolean;
  error: ChatError | null;
}

function AssistantMessageContent({
  content,
  actions,
}: {
  content: string;
  actions?: ActionResponse[];
}): ReactElement {
  const citations = extractCitations(content);

  return (
    <>
      <MarkdownRenderer content={content} />
      {citations.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1" data-testid="citation-list">
          {citations.map((citation) => (
            <CitationReference key={citation.raw} citation={citation} />
          ))}
        </div>
      )}
      {actions?.map((action, index) =>
        action.type === "prefill" ? (
          <PrefillCard key={`${action.formId}-${index}`} action={action} />
        ) : null
      )}
    </>
  );
}

export function ChatMessages({
  messages,
  isLoading,
  isStreaming,
  error,
}: ChatMessagesProps): ReactElement {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading, isStreaming]);

  return (
    <div
      ref={scrollRef}
      className="flex-1 overflow-y-auto p-3 space-y-3"
      data-testid="chat-messages"
    >
      {messages.length === 0 && !isLoading && (
        <div
          className="flex flex-col items-center justify-center h-full text-muted-foreground"
          data-testid="chat-empty"
        >
          <MessageCircle className="h-8 w-8 mb-2 opacity-50" />
          <p className="text-sm">Start a conversation</p>
        </div>
      )}

      {messages.map((msg) => (
        <div
          key={msg.id}
          className={cn("flex", msg.role === "user" ? "justify-end" : "justify-start")}
          data-testid={`chat-message-${msg.id}`}
        >
          <div
            className={cn(
              "max-w-[80%] rounded-lg px-3 py-2 text-sm",
              msg.role === "user"
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground"
            )}
          >
            {msg.role === "assistant" ? (
              <AssistantMessageContent
                content={msg.content}
                {...(msg.actions ? { actions: msg.actions } : {})}
              />
            ) : (
              msg.content
            )}
          </div>
        </div>
      ))}

      {isLoading && (
        <div className="flex justify-start" data-testid="chat-loading">
          <div className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            <span>Thinking...</span>
          </div>
        </div>
      )}

      {error && (
        <div
          className="flex items-center gap-2 rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive"
          data-testid="chat-error"
        >
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{error.message}</span>
        </div>
      )}
    </div>
  );
}
